import React, { useState, useRef, useEffect, useCallback } from "react";
import ChevronDownIcon from "../../assets/svg/chevron-down-icon";

export interface InputWithDropdownOption {
  value: string;
  content: string;
}

interface InputWithDropdownProps {
  options: InputWithDropdownOption[];
  value: string;
  onChange: (value: string, source: "dropdown" | "input") => void;
  onBlur?: () => void;
  id?: string;
  placeholder?: string;
  label?: string;
  labelClassName?: string;
  className?: string;
  triggerClassName?: string;
  dropdownClassName?: string;
  optionClassName?: string;
  inputClassName?: string;
  disabled?: boolean;
  error?: boolean;
  inputPattern?: RegExp;
}

const InputWithDropdown: React.FC<InputWithDropdownProps> = ({
  options,
  value,
  onChange,
  onBlur,
  id,
  placeholder = "Select or type",
  label,
  labelClassName = "",
  className = "",
  triggerClassName = "",
  dropdownClassName = "",
  optionClassName = "",
  inputClassName = "",
  disabled = false,
  error = false,
  inputPattern = /^[0-9]*$/,
}) => {
  const getDisplayValue = useCallback(
    (val: string): string => {
      if (!val) return "";
      const opt = options.find((o) => o.value === val);
      if (opt) return opt.content;
      return val;
    },
    [options]
  );

  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState(getDisplayValue(value));
  const [isTyping, setIsTyping] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);

  const dropdownRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const componentId =
    id || `input-dropdown-${Math.random().toString(36).slice(2, 9)}`;
  const triggerId = `${componentId}-trigger`;

  useEffect(() => {
    if (!isTyping) {
      setInputValue(getDisplayValue(value));
    }
  }, [value, isTyping, getDisplayValue]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setIsTyping(false);
        setFocusedIndex(-1);
      }
    };
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) setFocusedIndex(-1);
  }, [isOpen]);

  const commit = useCallback(
    (raw: string) => {
      const trimmed = raw.trim();
      if (!trimmed) {
        onChange("", "input");
        setInputValue("");
        setIsTyping(false);
        return;
      }
      const currentDisplay = getDisplayValue(value);
      if (trimmed === currentDisplay) {
        setIsTyping(false);
        return;
      }
      onChange(trimmed, "input");
      setInputValue(trimmed);
      setIsTyping(false);
    },
    [onChange, value, getDisplayValue]
  );

  const handleOptionSelect = useCallback(
    (opt: InputWithDropdownOption, e?: React.MouseEvent) => {
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }
      onChange(opt.value, "dropdown");
      setInputValue(opt.content);
      setIsTyping(false);
      setIsOpen(false);
      setFocusedIndex(-1);
    },
    [onChange]
  );

  const handleToggle = () => {
    if (disabled) return;
    setIsOpen((prev) => !prev);
    if (!isOpen) setTimeout(() => inputRef.current?.focus(), 100);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled) return;
    if (!inputPattern.test(e.target.value)) return;
    setInputValue(e.target.value);
    setIsTyping(true);
    setIsOpen(true);
  };

  const handleInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (triggerRef.current?.contains(e.relatedTarget as Node)) return;
    if (dropdownRef.current?.contains(e.relatedTarget as Node)) return;
    commit(inputValue);
    setIsOpen(false);
    onBlur?.();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (disabled) return;
    if (e.key === "Enter") {
      e.preventDefault();
      if (isOpen && focusedIndex >= 0) {
        handleOptionSelect(options[focusedIndex]);
      } else {
        commit(inputValue);
        setIsOpen(false);
        inputRef.current?.blur();
      }
    } else if (e.key === "Escape") {
      setIsOpen(false);
      setFocusedIndex(-1);
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      if (!isOpen) {
        setIsOpen(true);
        setFocusedIndex(0);
      } else {
        setFocusedIndex((prev) =>
          prev < options.length - 1 ? prev + 1 : 0
        );
      }
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      if (isOpen) {
        setFocusedIndex((prev) =>
          prev > 0 ? prev - 1 : options.length - 1
        );
      }
    }
  };

  return (
    <div className={`relative ${className}`}>
      {label && (
        <label
          htmlFor={triggerId}
          className={`flex items-center gap-[2px] font-normal text-verba-field-label dark:text-verba-field-label-dk mb-1 ${labelClassName}`}
        >
          {label}
        </label>
      )}
      <div
        ref={triggerRef}
        id={triggerId}
        className={`flex items-center justify-between w-full px-2 py-[7px] rounded-lg border cursor-pointer ${
          error
            ? "border-verba-red-alert dark:border-verba-red-alert-dk"
            : "border-border-gray dark:border-border-gray-dk"
        } ${disabled ? "opacity-80 cursor-not-allowed" : ""} ${triggerClassName}`}
        onClick={handleToggle}
      >
        <input
          ref={inputRef}
          value={inputValue}
          onChange={handleInputChange}
          onBlur={handleInputBlur}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          placeholder={placeholder}
          className={`flex-1 min-w-0 bg-transparent border-none outline-none text-md font-normal text-heading-text dark:text-heading-text-dk placeholder:text-gray-placeholder dark:placeholder:text-gray-placeholder-dk ${
            disabled ? "cursor-not-allowed" : ""
          } ${inputClassName}`}
        />
        <ChevronDownIcon
          className={`w-2 h-2 shrink-0 text-heading-text dark:text-heading-text-dk transition-transform duration-300 ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </div>
      {isOpen && (
        <div
          ref={dropdownRef}
          className={`absolute z-50 w-full mt-1 max-h-[200px] overflow-y-auto bg-popper-bg dark:bg-popper-bg-dk backdrop-blur-[10px] border border-border-gray dark:border-border-gray-dk rounded-[8px] shadow-popper-blur-effect ${dropdownClassName}`}
        >
          {options.map((opt, idx) => (
            <div
              key={opt.value}
              className={`flex items-center justify-between cursor-pointer text-md text-heading-text dark:text-heading-text-dk last:border-b-0 transition-colors px-[14px] py-[10px] min-w-0 ${
                value === opt.value
                  ? "bg-crm-card-highlight-2 dark:bg-crm-card-highlight-2-dk font-medium"
                  : focusedIndex === idx
                    ? "bg-hover-effect dark:bg-hover-effect-dk"
                    : "hover:bg-hover-effect dark:hover:bg-hover-effect-dk"
              } ${optionClassName}`}
              onMouseDown={(e) => handleOptionSelect(opt, e)}
              onMouseEnter={() => setFocusedIndex(idx)}
              onMouseLeave={() => setFocusedIndex(-1)}
            >
              {opt.content}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default InputWithDropdown;
